import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/sharp-solid-svg-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import FileDropZone from '@/components/fileDropZone';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP, ONE_HUNDRED_FIFTY_MB } from '@/constants/constants';
import { DELETE } from '@/constants/strings';
import { createSavedSelectionFeatureAttachment } from '@/endpoints/itasca/land/saved-selections/customizeOld/createSavedSelectionFeatureAttachment';
import { deleteSavedSelectionFeatureAttachment } from '@/endpoints/itasca/land/saved-selections/customizeOld/deleteSavedSelectionFeatureAttachment';
import { getSavedSelectionFeatureAttachments } from '@/endpoints/itasca/land/saved-selections/customizeOld/getSavedSelectionFeatureAttachments';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { DBFile } from '@/redux/slices/fileUploadModalSlice';
import { fileValidator } from '@/utils/fileValidator';
import Logger from '@/utils/logger';

import { useCustomizeLandContext } from '../../provider';
import CustomizeMediaPreview from './imagePreview';

const acceptedFileTypes = [
  'jpeg',
  'JPEG',
  'jpg',
  'JPG',
  'png',
  'PNG',
  'webp',
  'WEBP',
];
const acceptedFileMimeTypes = {
  'image/jpeg': ['.jpg', '.JPG', '.jpeg', '.JPEG'],
  'image/png': ['.png', '.PNG'],
  'image/webp': ['.webp', '.WEBP'],
};
const acceptedFileTypeStrings = ['JPG', 'PNG', 'WEBP'];

const SAVED_SELECTION_ATTACHMENT_LIMIT = 10;

const CustomizeSettingsMedia = () => {
  const { activeFile } = useFileManagementContext();
  const { activeFeature, updateDrawFeature, setActiveFeature } =
    useCustomizeLandContext();

  const [selectedMedia, setSelectedMedia] = useState<DBFile | null>(null);

  const [savedFiles, setSavedFiles] = useState<DBFile[]>([]);

  const getAttachmentsForFeature = useCallback(async () => {
    if (!activeFile || !activeFeature?.id) return;

    if (!Number(activeFeature.id)) return;

    const { res } = await getSavedSelectionFeatureAttachments(
      activeFile.id.toString(),
      activeFeature.id.toString()
    );

    if (res) {
      setSavedFiles(res.data);
    }
  }, [activeFeature, activeFile]);

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        if (!activeFeature || !activeFile) return;

        const fd = new FormData();
        fd.append('datafile', file);
        fd.append('name', file.name);

        const { res } = await createSavedSelectionFeatureAttachment({
          data: fd,
          feature_pk: Number(activeFeature.id),
          saved_selection_pk: activeFile.id,
        });

        if (res) {
          const updatedFeature = {
            ...activeFeature,
            properties: {
              ...activeFeature.properties,
              attachments: [
                ...(activeFeature.properties.attachments || []),
                res.data,
              ],
            },
          };
          updateDrawFeature(updatedFeature);
          setActiveFeature(updatedFeature);
          setSavedFiles((prev) => [...prev, res.data]);
        }
      } catch (err) {
        Logger.error(err);
      }
    },
    [activeFeature, activeFile, updateDrawFeature]
  );

  const numberOfSavedFiles = useMemo(
    () => savedFiles?.length || 0,
    [savedFiles]
  );

  const disableNewFileUpload = useMemo(
    () => numberOfSavedFiles >= SAVED_SELECTION_ATTACHMENT_LIMIT || !activeFile,
    [activeFile, numberOfSavedFiles]
  );

  const maxFileCanSelect = useMemo(() => {
    if (SAVED_SELECTION_ATTACHMENT_LIMIT - numberOfSavedFiles <= 0) {
      return 1; // 0 means no limitation
    }

    return SAVED_SELECTION_ATTACHMENT_LIMIT - numberOfSavedFiles;
  }, [numberOfSavedFiles]);

  const fileUploadToolTip = useMemo(() => {
    if (!activeFile) return 'Save map to upload media';
    return disableNewFileUpload
      ? 'Limit (10) reached. Remove to add more.'
      : '';
  }, [activeFile, disableNewFileUpload]);

  const deleteFile = useCallback(async () => {
    try {
      if (!activeFeature?.id || !activeFile || !selectedMedia) return;

      const { res } = await deleteSavedSelectionFeatureAttachment({
        feature_pk: activeFeature.id,
        fileId: selectedMedia.id,
        saved_selection_pk: activeFile.id,
      });

      if (res) {
        const updatedFeature = {
          ...activeFeature,
          properties: {
            ...activeFeature.properties,
            attachments: activeFeature.properties.attachments?.filter(
              (x) => x.id !== selectedMedia.id
            ),
          },
        };
        updateDrawFeature(updatedFeature);
        setActiveFeature(updatedFeature);
        setSavedFiles((prev) => prev.filter((x) => x.id !== selectedMedia.id));
        setSelectedMedia(null);
      }
    } catch (err) {
      Logger.error(err);
    }
  }, [activeFeature, activeFile, selectedMedia, updateDrawFeature]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        uploadFile(file);
      });
    },
    [uploadFile]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept: acceptedFileMimeTypes,
      disabled: disableNewFileUpload,
      maxFiles: maxFileCanSelect,
      maxSize: ONE_HUNDRED_FIFTY_MB,
      multiple: true,
      onDrop,
      validator: fileValidator(
        acceptedFileTypes,
        undefined,
        ONE_HUNDRED_FIFTY_MB
      ),
    });

  const groupedFileRejections = useMemo(() => {
    const grouped = fileRejections?.reduce((acc, curr) => {
      const key = curr.errors[0].message;
      if (!acc[key]) {
        // eslint-disable-next-line no-param-reassign
        acc[key] = [];
      }
      acc[key].push(curr);
      return acc;
    }, {} as Record<string, any[]>);

    return grouped;
  }, [fileRejections]);

  useEffect(() => {
    getAttachmentsForFeature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeFeature) return;

    getAttachmentsForFeature();
  }, [activeFeature, getAttachmentsForFeature]);

  return (
    <div
      className={twMerge('flex flex-col gap-2', !activeFile && ' opacity-50')}
    >
      <Typography
        className={twMerge(
          'text-right',
          disableNewFileUpload && 'text-red-utility'
        )}
        variant="body3"
      >
        {numberOfSavedFiles} / {SAVED_SELECTION_ATTACHMENT_LIMIT}
      </Typography>

      <div
        data-tooltip-content={fileUploadToolTip}
        data-tooltip-id={GLOBAL_TOOLTIP}
      >
        <FileDropZone
          acceptedFiles={acceptedFileTypeStrings}
          className="h-[93px]"
          disabled={disableNewFileUpload}
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          maxFileSizeString="150MB"
          showFileIcon={false}
        />
      </div>
      <div>
        {Object.keys(groupedFileRejections).map((error) => (
          <Typography
            key={error}
            className="text-red-utility"
            variant="caption"
          >
            {error} ({groupedFileRejections[error].length} file(s))
          </Typography>
        ))}
      </div>
      {/* Preview */}
      <div className="flex flex-1 flex-wrap gap-3">
        {savedFiles?.map((file) => (
          <CustomizeMediaPreview
            key={file.id}
            checkMark
            active={file === selectedMedia}
            className="h-20 w-20"
            src={file.url}
            onClick={() => {
              setSelectedMedia(file);
            }}
          />
        ))}
      </div>
      {savedFiles && (
        <div className="flex justify-end">
          <Button
            className="flex gap-x-2 text-red-utility"
            disabled={!selectedMedia}
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={deleteFile}
          >
            {DELETE} <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomizeSettingsMedia;
